import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DriverLoadingPage, triggerToastDS } from "@qivia/ui";
import { VehicleRestitutionReturnStep } from "./vehicleRestitutionReturnStep";
import { VehicleRestitutionConditionStep } from "./vehicleRestitutionConditionStep";
import { VehicleRestitutionSummaryStep } from "./vehicleRestitutionSummaryStep";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { selectVehicleUuid } from "../../qrCode/vehicleLoginSlice";
import {
  createInitialVehicleRestitutionAsync,
  selectCreateInitialVehicleRestitutionStatus,
  vehicleSessionsSlice,
} from "./vehicleSessionsSlice";
import { ErrorPage } from "../../components/ErrorPage";

type RestitutionStep = "return" | "condition" | "summary";

export const VehicleRestitutionPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const vehicleUuid = useAppSelector(selectVehicleUuid);
  const createInitialVehicleRestitutionStatus = useAppSelector(
    selectCreateInitialVehicleRestitutionStatus,
  );
  const [step, setStep] = useState<RestitutionStep>("return");
  const [departureLocation, setDepartureLocation] = useState("");
  const [furthestDestination, setFurthestDestination] = useState("");
  const [mileage, setMileage] = useState("");
  const [isCabinCleaned, setIsCabinCleaned] = useState(false);
  const [isTrunkCleaned, setIsTrunkCleaned] = useState(false);
  const [observations, setObservations] = useState("");
  const [photos, setPhotos] = useState<File[]>([]);

  useEffect(() => {
    if (createInitialVehicleRestitutionStatus === "success") {
      setStep("summary");
      dispatch(
        vehicleSessionsSlice.actions.resetCreateInitialVehicleRestitutionStatus(),
      );
    }
    if (createInitialVehicleRestitutionStatus === "failed") {
      triggerToastDS(t("restitution.summary.error.text") || "", "ERROR");
      dispatch(
        vehicleSessionsSlice.actions.resetCreateInitialVehicleRestitutionStatus(),
      );
    }
  }, [createInitialVehicleRestitutionStatus, t, dispatch]);

  if (!vehicleUuid) return <ErrorPage page="qrCode" />;

  const submitRestitution = async () => {
    const pictures = await Promise.all(
      photos.map(async (file) => {
        const buffer = await file.arrayBuffer();
        const bytes = new Uint8Array(buffer);
        const fileBuffer = btoa(
          Array.from(bytes, (b) => String.fromCharCode(b)).join(""),
        );
        return { originalFileName: file.name, fileBuffer };
      }),
    );
    void dispatch(
      createInitialVehicleRestitutionAsync({
        vehicleUuid,
        mileage: Number(mileage),
        isCabinCleaned,
        isTrunkCleaned,
        observations: observations === "" ? null : observations,
        departureLocation,
        furthestDestination:
          furthestDestination === "" ? null : furthestDestination,
        pictures,
      }),
    );
  };

  switch (step) {
    case "return":
      return (
        <VehicleRestitutionReturnStep
          departureLocation={departureLocation}
          setDepartureLocation={setDepartureLocation}
          furthestDestination={furthestDestination}
          setFurthestDestination={setFurthestDestination}
          mileage={mileage}
          setMileage={setMileage}
          onReturn={() => navigate(-1)}
          onSubmit={() => setStep("condition")}
        />
      );
    case "condition":
      if (createInitialVehicleRestitutionStatus === "processing") {
        return <DriverLoadingPage />;
      }
      return (
        <VehicleRestitutionConditionStep
          isCabinCleaned={isCabinCleaned}
          setIsCabinCleaned={setIsCabinCleaned}
          isTrunkCleaned={isTrunkCleaned}
          setIsTrunkCleaned={setIsTrunkCleaned}
          observations={observations}
          setObservations={setObservations}
          photos={photos}
          setPhotos={setPhotos}
          onReturn={() => setStep("return")}
          onValidate={() => void submitRestitution()}
        />
      );
    case "summary":
      return (
        <VehicleRestitutionSummaryStep
          departureLocation={departureLocation}
          furthestDestination={furthestDestination}
          mileage={mileage}
          isCabinCleaned={isCabinCleaned}
          isTrunkCleaned={isTrunkCleaned}
          observations={observations}
          photos={photos}
        />
      );
  }
};
